import { Inject, Injectable, Optional } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';

import { CustomCategories, MenuCategoriesState } from '../models/category-page.models';

import { Category } from '@core/domain/models/category.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { routerPublicModels } from '@frontend/libs/duttirouter';
import { CategoryNavMenus } from '@pages/category/models/category-nav-menus.model';
import { MenuCategory } from '@shared/header/models/category.model';

@Injectable()
export class CategoryPageService {
    constructor(
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Optional() @Inject('ECOMMERCE_CONTEXT') private readonly context,
    ) { }

    public parseCategoryId(data: routerPublicModels.MergedRoute, categoriesNavigation: MenuCategory[]): string {
        const celement: string = data.queryParams.celement;
        if (celement) {
            const categoryByTree = categoriesNavigation.find((cat) => celement === cat.id);
            if (categoryByTree?.id) {
                return categoryByTree.id;
            }
        }
        let categoryId: string = data.params.categoryId ?? this.context?.categoryId;
        if (!categoryId) {
            const categoryByTree = categoriesNavigation.find((cat) =>
                data.url.includes(cat.locationPath) && !cat.categoryUrlParam);
            categoryId = categoryByTree?.id ?? this.window?.inditex?.iCategoryId;

            if (!categoryId) {
                return null;
            }
        }
        return categoryId.toString();
    }

    public static parseMenuCategories(category: Category, _categories: MenuCategory[]): MenuCategoriesState {
        // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
        const subCategories = !!category.id ? CategoryPageService.getSubcategories(_categories, category.id) : [];
        const categories = JSON.parse(JSON.stringify(_categories));
        const navMenuCategories: CategoryNavMenus = {
            categories,
            subCategories,
        };
        return {
            navMenuCategories,
            filterTypeUsedAsSubcategories: CategoryPageService.getCurrentCategoryFilterType(category) || null,
        };
    }

    public static getCustomCategory(category: Category): string {
        return category?.attachments?.find((attachment) => Object.values(CustomCategories).includes(attachment?.name))?.name;
    }

    private static getCurrentCategoryFilterType(currentCategory: Category): string {
        const filterPath = currentCategory.attachments?.find((cc: Category) => cc.name.includes('CATEGORY_FILTER'))?.path;
        return filterPath;
    }

    private static getSubcategories(categories: MenuCategory[], categoryId: string): MenuCategory[] {
        // biome-ignore lint/complexity/noThisInStatic: <explanation>
        const cat = this.findCategory(categories, categoryId);
        let subcategories = cat?.subcategories;
        if (subcategories === undefined || subcategories.length === 0) {
            // biome-ignore lint/complexity/noThisInStatic: <explanation>
            subcategories = this.getSubcategories(categories, cat.parentId);
        }
        return subcategories;
    }

    private static findCategory(categories: MenuCategory[], categoryId: string): MenuCategory {
        for (const cat of categories) {
            if (cat.id === categoryId) return cat;
            // biome-ignore lint/complexity/noThisInStatic: <explanation>
            const _cat = this.findCategory(cat?.subcategories ?? [], categoryId);
            if (_cat) {
                return _cat;
            }
        }
    }
}
